import React, { useState, useRef } from 'react';
import { uploadCsv, exportQueueEmails } from '../api/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { UploadCloud, CheckCircle, AlertCircle, FileSpreadsheet, X, FileText, Download } from 'lucide-react';
import { Progress } from './ui/progress';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import Papa from 'papaparse';

export default function CsvUpload() {
    const [file, setFile] = useState(null);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [diagnostics, setDiagnostics] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const inputRef = useRef(null);

    const handleFile = (selectedFile) => {
        setSuccess('');
        setError('');
        setProgress(0);
        setDiagnostics(null);

        if (selectedFile && (selectedFile.type === 'text/csv' || selectedFile.name.endsWith('.csv'))) {
            setFile(selectedFile);
        } else {
            setError('Lütfen geçerli bir CSV dosyası yükleyin.');
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFile(e.dataTransfer.files[0]);
        }
    };

    const handleChange = (e) => {
        e.preventDefault();
        if (e.target.files && e.target.files[0]) {
            handleFile(e.target.files[0]);
        }
    };

    const onButtonClick = () => {
        inputRef.current.click();
    };

    const removeFile = () => {
        setFile(null);
        setSuccess('');
        setError('');
        setDiagnostics(null);
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        setSuccess('');
        setError('');
        if (!file) {
            setError('Lütfen bir dosya seçin!');
            return;
        }
        setLoading(true);
        setProgress(0);
        setDiagnostics(null);

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: async (results) => {
                const data = results.data;
                const totalRows = data.length;

                if (totalRows === 0) {
                    setError("CSV dosyası boş.");
                    setLoading(false);
                    return;
                }

                const chunkSize = 1000;
                let processed = 0;
                let totalUploaded = 0;
                let aggr = {
                    total: totalRows,
                    created: 0,
                    matched: 0,
                    updated: 0,
                    skipped: 0,
                    conflicts: 0,
                    details: []
                };

                try {
                    for (let i = 0; i < totalRows; i += chunkSize) {
                        const chunk = data.slice(i, i + chunkSize);
                        const csvContent = Papa.unparse(chunk);
                        const blob = new Blob([csvContent], { type: 'text/csv' });
                        const chunkFile = new File([blob], `chunk_${i}.csv`, { type: 'text/csv' });

                        const res = await uploadCsv(chunkFile, i + 2); // Excel offsets: index 0 is row 2
                        totalUploaded += res.data.count || 0;
                        processed += chunk.length;

                        // Aggregate structured diagnostics safely
                        if (res.data.details) {
                            aggr.created += res.data.details.created || 0;
                            aggr.matched += res.data.details.matched || 0;
                            aggr.updated += res.data.details.updated || 0;
                            aggr.skipped += res.data.details.skipped || 0;
                            aggr.conflicts += res.data.details.conflicts || 0;
                            if (res.data.details.details && Array.isArray(res.data.details.details)) {
                                aggr.details = aggr.details.concat(res.data.details.details);
                            }
                        }

                        setProgress(Math.min(Math.round((processed / totalRows) * 100), 100));
                    }

                    setDiagnostics(aggr);
                    setSuccess(`Yükleme Tamamlandı! Toplam ${totalRows} satırdan ${totalUploaded} kayıt havuza devredildi.`);
                    setFile(null);
                    toast.success("Tüm parçalar başarıyla yüklendi!");
                } catch (err) {
                    console.error(err);
                    setError('Parça işleme sırasında yükleme hatası. Bazı parçalar yüklenmiş olabilir.');
                    toast.error("Yükleme kesildi.");
                } finally {
                    setLoading(false);
                }
            },
            error: (err) => {
                console.error(err);
                setError('CSV dosyası ayrıştırılamadı.');
                setLoading(false);
            }
        });
    };

    const handleExport = async () => {
        try {
            toast.loading("Veri dışa aktarılıyor...");
            const response = await exportQueueEmails();
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `contacts_export_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.dismiss();
            toast.success("Dışa aktarma başarılı!");
        } catch (error) {
            console.error(error);
            toast.dismiss();
            toast.error("Veri dışa aktarılamadı.");
        }
    };

    const handleDownloadRepairReport = () => {
        if (!diagnostics || !diagnostics.details) return;

        const itemsToRepair = diagnostics.details.filter(d =>
            d.status === 'SKIPPED' || d.status === 'ERROR' || d.status === 'CONFLICT'
        );

        if (itemsToRepair.length === 0) {
            toast.info("Onarılacak hatalı kayıt yok.");
            return;
        }

        const csvContent = Papa.unparse(itemsToRepair.map(d => ({
            'Row Index (Excel)': d.rowIndex || '-',
            'Email': d.email || '-',
            'Status': d.status,
            'Message': d.message
        })));

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `repair-report-${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success("Onarım raporu indirildi!");
    };

    return (
        <div className="flex flex-col justify-center items-center min-h-[calc(100vh-100px)] p-6 animate-in fade-in zoom-in duration-300 gap-8">
            <Card className="w-full max-w-lg shadow-xl border-t-4 border-t-primary">
                <CardHeader className="text-center pb-2">
                    <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4 transition-transform hover:scale-110 duration-300">
                        <UploadCloud className="h-8 w-8 text-primary" />
                    </div>
                    <CardTitle className="text-2xl font-bold">Rehber İçe Aktar</CardTitle>
                    <CardDescription className="text-base">
                        Toplu email yüklemek için CSV dosyanızı yükleyin.
                        <br /><span className="text-xs text-muted-foreground">(1000'erli parçalar halinde işlenir)</span>
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">

                    {/* Drag & Drop Zone */}
                    <div
                        className={cn(
                            "relative flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-xl transition-all duration-300 ease-in-out cursor-pointer overflow-hidden",
                            dragActive ? "border-primary bg-primary/5 scale-[1.02]" : "border-muted-foreground/25 hover:bg-muted/30 hover:border-primary/50",
                            file ? "border-primary/50 bg-primary/5" : ""
                        )}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        onClick={!file ? onButtonClick : undefined}
                    >
                        <Input
                            ref={inputRef}
                            type="file"
                            accept=".csv"
                            className="hidden"
                            onChange={handleChange}
                        />

                        {file ? (
                            <div className="flex flex-col items-center animate-in fade-in zoom-in duration-300">
                                <div className="p-3 bg-background border border-border rounded-full shadow-sm mb-2">
                                    <FileSpreadsheet className="h-8 w-8 text-green-600" />
                                </div>
                                <p className="font-semibold text-sm max-w-[200px] truncate px-2">{file.name}</p>
                                <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(2)} KB</p>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="absolute top-2 right-2 h-8 w-8 p-0 rounded-full hover:bg-destructive/10 hover:text-destructive transition-colors"
                                    onClick={(e) => { e.stopPropagation(); removeFile(); }}
                                >
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center text-center p-4">
                                <UploadCloud className={cn("h-10 w-10 mb-3 text-muted-foreground", dragActive && "text-primary animate-bounce")} />
                                <p className="text-sm font-medium text-foreground">
                                    <span className="text-primary hover:underline">Yüklemek için tıklayın</span> veya sürükleyip bırakın
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Sadece CSV dosyaları (maks 10MB)
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Format Info */}
                    {!file && !success && (
                        <div className="bg-muted/40 p-3 rounded-lg flex items-start space-x-3 text-sm text-muted-foreground border">
                            <FileText className="h-4 w-4 mt-0.5 shrink-0" />
                            <div>
                                <p className="font-semibold">Gerekli CSV Formatı:</p>
                                <p className="text-xs mt-1">Başlıklar: <code className="bg-muted px-1 py-0.5 rounded">email</code>, <code className="bg-muted px-1 py-0.5 rounded">first_name</code>, <code className="bg-muted px-1 py-0.5 rounded">last_name</code></p>
                            </div>
                        </div>
                    )}

                    {loading && (
                        <div className="space-y-2">
                            <div className="flex justify-between text-xs text-muted-foreground">
                                <span>Yükleniyor...</span>
                                <span>{progress}%</span>
                            </div>
                            <Progress value={progress} className="h-2 w-full transition-all" />
                        </div>
                    )}

                    {success && (
                        <Alert className="bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-900">
                            <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                            <AlertTitle className="text-green-600 dark:text-green-400">Yükleme Tamamlandı</AlertTitle>
                            <AlertDescription className="text-green-600 dark:text-green-400 text-xs mt-1">
                                {success}
                            </AlertDescription>
                        </Alert>
                    )}

                    {error && (
                        <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertTitle>Hata</AlertTitle>
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    {diagnostics && (
                        <div className="mt-6 border rounded-xl overflow-hidden shadow-sm bg-white">
                            <div className="bg-slate-50 border-b px-4 py-3 flex items-center gap-2">
                                <FileSpreadsheet className="w-5 h-5 text-purple-600" />
                                <h3 className="font-semibold text-slate-800">İçe Aktarım Özeti</h3>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-0 bg-slate-100">
                                <div className="bg-white p-4 border-r border-b">
                                    <p className="text-xs uppercase text-muted-foreground font-semibold mb-1">Toplam İşlenen</p>
                                    <p className="text-xl font-bold">{diagnostics.total}</p>
                                </div>
                                <div className="bg-white p-4 sm:border-r border-b">
                                    <p className="text-xs uppercase text-green-600 font-semibold mb-1">Yeni Oluşturulan</p>
                                    <p className="text-xl font-bold">{diagnostics.created}</p>
                                </div>
                                <div className="bg-white p-4 border-r sm:border-r-0 border-b">
                                    <p className="text-xs uppercase text-blue-600 font-semibold mb-1">Eşleşen (Mevcut)</p>
                                    <p className="text-xl font-bold">{diagnostics.matched}</p>
                                </div>
                                <div className="bg-white p-4 sm:border-r">
                                    <p className="text-xs uppercase text-indigo-600 font-semibold mb-1">Güncellenen</p>
                                    <p className="text-xl font-bold">{diagnostics.updated}</p>
                                </div>
                                <div className="bg-white p-4 border-r sm:border-r-0">
                                    <p className="text-xs uppercase text-orange-600 font-semibold mb-1 justify-between flex items-center">
                                        Sorunlu/Atlanan
                                        {diagnostics.skipped > 0 && <AlertCircle className="w-3.5 h-3.5 inline text-orange-500" />}
                                    </p>
                                    <p className="text-xl font-bold">{diagnostics.skipped}</p>
                                </div>
                                <div className="bg-white p-4 sm:border-t sm:-mt-[1px]">
                                    <p className="text-xs uppercase text-red-600 font-semibold mb-1">Email Çakışması</p>
                                    <p className="text-xl font-bold">{diagnostics.conflicts}</p>
                                </div>
                            </div>

                            {diagnostics.details && diagnostics.details.length > 0 && (
                                <div className="p-4 bg-slate-50 border-t">
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="text-xs font-semibold uppercase text-slate-500">Detay Logları {diagnostics.details.length > 50 ? '(İlk 50 Gösteriliyor)' : ''}</h4>
                                        {(diagnostics.skipped > 0 || diagnostics.conflicts > 0) && (
                                            <Button variant="outline" size="sm" onClick={handleDownloadRepairReport} className="text-orange-600 border-orange-200 hover:bg-orange-50 h-7 text-xs">
                                                <Download className="w-3 h-3 mr-1" /> Onarım Raporunu İndir
                                            </Button>
                                        )}
                                    </div>
                                    <div className="max-h-48 overflow-y-auto space-y-2 pr-2 text-sm">
                                        {diagnostics.details.slice(0, 50).map((log, i) => (
                                            <div key={i} className={cn(
                                                "p-2 rounded border text-xs",
                                                log.status === 'ERROR' || log.status === 'CONFLICT' || log.status === 'SKIPPED' ? "bg-red-50 border-red-100 text-red-700" :
                                                    log.status === 'WORKSPACE_ALREADY_LINKED' ? "bg-slate-100 border-slate-200 text-slate-600" :
                                                        "bg-blue-50 border-blue-100 text-blue-700"
                                            )}>
                                                <span className="font-bold mr-2">[{log.status}]</span>
                                                {log.message ? `Satır ${log.rowIndex || '?'}: ${log.message}` : `Satır ${log.rowIndex || '?'}: Linked ID ${log.personId}`}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <Button
                        onClick={handleSubmit}
                        className="w-full h-11 text-base font-semibold shadow-md transition-all hover:scale-[1.02]"
                        disabled={loading || !file}
                    >
                        {loading ? 'İşleniyor...' : 'Listeyi Yükle'}
                    </Button>

                </CardContent>
            </Card>

            <Card className="w-full max-w-lg shadow-md border-t-4 border-t-green-600">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div className="flex items-center gap-4">
                        <div className="p-2 rounded-full bg-green-100 dark:bg-green-900/30">
                            <Download className="h-6 w-6 text-green-600 dark:text-green-400" />
                        </div>
                        <div>
                            <CardTitle className="text-lg font-bold">Veritabanını Dışa Aktar</CardTitle>
                            <CardDescription>Tüm kişileri CSV olarak indir.</CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <Button variant="outline" onClick={handleExport} className="w-full text-green-700 hover:text-green-800 hover:bg-green-50 border-green-200">
                        <Download className="mr-2 h-4 w-4" /> Tam CSV İndir
                    </Button>
                </CardContent>
            </Card>
        </div>
    );
} 